import { Injectable, OnDestroy } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { CalloutType, FeatureCallout, FeatureCalloutAction } from '../../feature-callout/feature-callout.model';
import { FeatureCalloutService } from '../../feature-callout/feature-callout.service';
import { InstrumentationParam, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { UserProfileService } from '../../shared/instrumentation/user-profile.service';
import { ObservableScope } from '../../shared/models/observable-scope.enum';
import { ConsumerSkuType } from '../../shared/services/consumer-license.service';
import { DeviceDetector } from '../../shared/services/device-detector.service';
import { DocumentService } from '../../shared/services/document.service';
import { EventService } from '../../shared/services/event.service';
import { PerformanceService } from '../../shared/services/performance.service';
import { ShellShimService } from '../../shared/services/shell-shim.service';
import { ThemingService } from '../../shared/services/theming.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { LocService } from '../../shared/shared-context/loc.service';
import { ConsumerUserConsentGroup, UserConsentConfig, UserConsentConfigService } from '../../shared/shared-context/user-consent-config.service';
import { UserContextService } from '../../shared/shared-context/user-context.service';
import { FirstRunConfigService } from '../first-run-component/first-run-config.service';
import { FirstRunData, FirstRunDataService } from '../shared/first-run-data.service';
import { FirstRunCalloutsConfig, FirstRunCalloutsConfigService } from './first-run-callouts-config.service';

const enum TuiFlowInTestMode {
    TestModeDisabled = '0',
    V1 = '1',
    V2 = '2',
    ReturningUser = '3',
    ReturningUserReact = '4',
    NewUserReact = '5'
}

@Injectable({
    providedIn: 'root'
})
export class FirstRunCalloutsService implements OnDestroy {
    private readonly installButtonResponsePromise: Promise<string>;
    private readonly modalFreClosedPromise: Promise<void>;
    private readonly appBarShowingPromise: Promise<void>;
    private readonly consumerSubscriptionStatusPromise: Promise<ConsumerSkuType>;
    private readonly userConsentPromise: Promise<UserConsentConfig>;
    private readonly disabled: boolean;
    private readonly isCommercialUser: boolean;
    private readonly config: FirstRunCalloutsConfig;

    private firstRunVersion: number;
    private initialized = false;
    private showModalFre: boolean;

    private theme: string;
    private themeShadesSubscription: Subscription;

    private appLauncherElement: HTMLElement;
    private adminCenterButtonElement: HTMLElement;
    private createNewButtonElement: HTMLElement;
    private myContentButtonElement: HTMLElement;
    private uploadButtonElement: HTMLElement;
    private consumerBuyButtonElement: HTMLElement;
    private installButtonElement: HTMLElement;
    private feedbackContainerElement: HTMLElement;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly contextService: ContextService,
        private readonly documentService: DocumentService,
        private readonly eventService: EventService,
        private readonly featureCalloutService: FeatureCalloutService,
        private readonly firstRunConfigService: FirstRunConfigService,
        private readonly firstRunDataService: FirstRunDataService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly performanceService: PerformanceService,
        private readonly shellShimService: ShellShimService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly themingService: ThemingService,
        private readonly userConsentService: UserConsentConfigService,
        private readonly userContextService: UserContextService,
        private readonly userProfileService: UserProfileService,
        deviceDetector: DeviceDetector,
        firstRunCalloutsConfigService: FirstRunCalloutsConfigService
    ) {
        this.disabled = !deviceDetector.isDesktopDevice()
            || !firstRunCalloutsConfigService.config
            || firstRunCalloutsConfigService.config.hideInTestMode
            || this.capabilitiesService.capabilities.teachingUiReactNewUserEnabled;
        if (this.disabled) {
            return;
        }

        this.config = firstRunCalloutsConfigService.config;

        this.isCommercialUser = this.startupConfigService.config.authType === AuthType.OrgId;
        this.theme = 'light';

        // Track Promises
        this.installButtonResponsePromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.installButtonResponse),
            take(1))
            .toPromise()
            .then(event => {
                if (event.args && event.args.length) {
                    return Promise.resolve(event.args[0]);
                }
            });

        this.consumerSubscriptionStatusPromise = this.consumerInstallConfigService.getConfig().then(config => config && config.currentSkuInfo && config.currentSkuInfo.skuType);

        this.userConsentPromise = !this.isCommercialUser ? this.userConsentService.getConfig() : Promise.resolve(null);

        this.modalFreClosedPromise = this.firstRunConfigService.getConfig().then(firstRunConfig => {
            this.showModalFre = !!firstRunConfig && !!firstRunConfig.model && !!firstRunConfig.model.slides;
            if (this.showModalFre) {
                return eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.firstRunModalClosed),
                    take(1))
                    .toPromise()
                    .then(() => Promise.resolve());
            } else {
                return Promise.resolve();
            }
        });

        if (this.capabilitiesService.capabilities.appBarEnabled) {
            this.appBarShowingPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.appBarReady),
                take(1))
                .toPromise()
                .then(() => Promise.resolve());
        } else {
            this.appBarShowingPromise = Promise.resolve();
        }
    }

    public ngOnDestroy() {
        this.themeShadesSubscription?.unsubscribe();
    }

    public async initializeFirstRun(): Promise<void> {
        // The shell is different for mobile/tablet scenarios and first run is less relevant
        if (this.disabled || this.initialized) {
            return;
        }

        this.initialized = true;

        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.themeShadesSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                this.theme = event ? 'dark' : 'light';
            });
        }

        this.firstRunVersion = this.config.version;

        const firstRunData = await this.fetchFirstRun();

        const calloutsPromise = this.getCallouts(firstRunData.CompletedFirstRun);

        const callouts = await calloutsPromise;

        if (callouts.length) {
            this.initCalloutActions(callouts);

            this.setupFirstCallout(firstRunData, callouts);

            this.featureCalloutService.addCallouts(callouts);
        }

        this.performanceService.mark('FRE_Ready');
    }

    public fetchFirstRun(): Promise<FirstRunData> {
        return this.firstRunDataService.fetchFirstRunData(this.firstRunVersion, false, FirstRunDataService.firstRunDataKey);
    }

    public shouldShowCallouts(): Promise<boolean> {
        // If disabled no angular callouts will be shown
        if (this.disabled) {
            return Promise.resolve(false);
        }
        const firstRunDataPromise = this.fetchFirstRun();
        const hasSeenNewCalloutPromise = this.fetchHasSeenNewCallouts();
        const { tuiFlowInTestMode } = this.config;
        const willShowAngularTuiTestCallouts = tuiFlowInTestMode === TuiFlowInTestMode.V2 || tuiFlowInTestMode === TuiFlowInTestMode.ReturningUser;
        return willShowAngularTuiTestCallouts ? Promise.resolve(true) : Promise.all([firstRunDataPromise, hasSeenNewCalloutPromise])
                      .then(([firstRunData, hasSeenNewCallouts]) => !firstRunData.CompletedFirstRun || !hasSeenNewCallouts);
    }

    private fetchHasSeenNewCallouts(): Promise<boolean> {
        return this.userProfileService.doesMetricExist(KnownInstrumentationEvents.FreNewCalloutsImpression);
    }

    private getCallouts(isFirstRunCompleted: boolean): Promise<Array<FeatureCallout>> {
        const { tuiFlowInTestMode } = this.config;

        if (tuiFlowInTestMode === TuiFlowInTestMode.TestModeDisabled) {
            if (isFirstRunCompleted) {
                return this.getReturningUserCallouts();
            } else {
                return this.getFirstRunCallouts();
            }
        }

        if (tuiFlowInTestMode === TuiFlowInTestMode.V2) {
            return this.getFirstRunCallouts();
        }
        if (tuiFlowInTestMode === TuiFlowInTestMode.ReturningUser) {
            return this.getReturningUserCallouts();
        }
        if (tuiFlowInTestMode === TuiFlowInTestMode.ReturningUserReact || tuiFlowInTestMode === TuiFlowInTestMode.NewUserReact) {
            return Promise.resolve([]);
        }
    }

    /**
     * Returns a promise with an array of the callouts that should be shown to the user on first run. New content for Office 2.0
     */
    private getFirstRunCallouts(): Promise<Array<FeatureCallout>> {
        const isEduUser = this.userContextService.user.isEdu;

        return new Promise<Array<FeatureCallout>>((resolve, reject) => {
            Promise.all([
                this.shellShimService.afterShellPlusLoaded,
                this.modalFreClosedPromise,
                this.consumerSubscriptionStatusPromise,
                this.userConsentPromise,
                this.installButtonResponsePromise,
                this.appBarShowingPromise
            ]).then(([shellResult, _, consumerSubscriptionStatus, consentConfig]) => {
                    const callouts = new Array<FeatureCallout>();
                    const includeShellCallouts = shellResult;
                    const isFreeConsumer = this.isFreeConsumer(consumerSubscriptionStatus);
                    const userCanViewUpsell = this.userCanViewUpsell(isFreeConsumer, consentConfig);

                    this.detectDomElements();

                    if (includeShellCallouts && this.appLauncherElement) {
                        const findMoreAppsSvg = !this.isCommercialUser ? this.config.findMoreAppsConsumerSvg[this.theme] : isEduUser ? this.config.findMoreAppsEduSvg[this.theme] : this.config.findMoreAppsCommercialSvg[this.theme];
                        const subtitle = this.isCommercialUser ? this.locService.FirstRunFindMoreAppsSubtitleCommercial : this.locService.FirstRunFindMoreAppsSubtitleConsumer;

                        callouts.push(new FeatureCallout(
                            this.appLauncherElement,
                            CalloutType.AppLauncher,
                            KnownAreas.FirstRun,
                            this.locService.FirstRunFindMoreAppsTitle,
                            subtitle,
                            null, null, null, null, null, false, null, null, null, null, null, null, null, findMoreAppsSvg
                        ));
                    }

                    if (this.adminCenterButtonElement && this.userContextService.user.isAdmin) {
                        callouts.push(new FeatureCallout(
                            this.adminCenterButtonElement,
                            CalloutType.AdminTile,
                            KnownAreas.FirstRun,
                            isEduUser ? this.locService.FirstRunManageSchoolTitle : this.locService.FirstRunManageOrganizationTitle,
                            this.locService.FirstRunManageOrganizationSubtitleNext,
                            null, null, null, null, null, true, null, null, null, null, -240, null, 240, this.config.adminCenterSvg[this.theme]
                        ));
                    }

                    if (this.createNewButtonElement) {
                        callouts.push(new FeatureCallout(
                            this.createNewButtonElement,
                            CalloutType.NewButton,
                            KnownAreas.FirstRun,
                            this.locService.FirstRunCreateHereTitle,
                            this.locService.FirstRunCreateHereSubtitle,
                            null, null, null, null, null, true, null, null, null, null, null, null, null, this.config.createSvg[this.theme]));
                    }

                    if (this.myContentButtonElement && this.isCommercialUser) {
                        callouts.push(new FeatureCallout(
                            this.myContentButtonElement,
                            CalloutType.MyContent,
                            KnownAreas.FirstRun,
                            this.locService.FirstRunManageContentTitle,
                            this.locService.FirstRunManageContentSubtitle,
                            null, null, null, null, null, true, null, null, null, null, null, null, null, this.config.manageContentSvg[this.theme]));
                    }

                    if (this.uploadButtonElement) {
                        callouts.push(new FeatureCallout(
                            this.uploadButtonElement,
                            CalloutType.Upload,
                            KnownAreas.FirstRun,
                            this.locService.FirstRunUploadExistingContentTitle,
                            this.locService.FirstRunUploadExistingContentSubtitle,
                            null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.uploadContentSvg[this.theme]));
                    }

                    if (this.consumerBuyButtonElement) {
                        if (userCanViewUpsell) {
                            const subtitle = this.locService.FirstRunUpgradeAnyTimeSubtitleTemp;

                            callouts.push(new FeatureCallout(
                                this.consumerBuyButtonElement,
                                CalloutType.BuyOffice,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunUpgradeAnyTimeTitle,
                                subtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.upgradeSvg[this.theme]));
                        }
                    }

                    if (this.installButtonElement) {
                        if (!isFreeConsumer && this.config.installButtonCalloutEnabled) {
                            callouts.push(new FeatureCallout(
                                this.installButtonElement,
                                CalloutType.Install,
                                KnownAreas.FirstRun,
                                this.locService.FirstRunDownloadAppsTitle,
                                this.locService.FirstRunDownloadAppsSubtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.officeMobileAppSvg[this.theme]));
                        }
                    }

                    if (this.feedbackContainerElement) {
                        callouts.push(new FeatureCallout(
                            this.feedbackContainerElement,
                            CalloutType.Feedback,
                            KnownAreas.FirstRun,
                            this.locService.FirstRunYoureAllSetTitle,
                            this.locService.FirstRunYoureAllSetSubtitle,
                            null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.allSetSvg[this.theme], true));
                    }

                    this.userProfileService.incrementMetric(KnownInstrumentationEvents.FreNewCalloutsImpression);
                    if (this.capabilitiesService.capabilities.quickAccessV1Enabled && !this.isCommercialUser) {
                        // If quick access is enabled in consumer and the user is going through the FRE flow they have never known
                        // the "favorites" tab as "pinned" so we mark it so we don't show the callout that explains that it has changed.
                        this.userProfileService.incrementMetric(KnownInstrumentationEvents.ConsumerPinnedFavoritesSequenceImpression);
                    }

                    const params = {};
                    params[InstrumentationParam.FirstRunTotalSteps] = callouts.length.toString();
                    params[InstrumentationParam.State] = 'first-run';

                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FreNewCalloutsEvent, params);
                        // TODO: Remove above logger once OTEL migration is complete
                        logFeature({
                            featureName: KnownFeatureNames.FirstRun,
                            eventName: KnownInstrumentationEvents.FreNewCalloutsEvent,
                            params
                        });

                    resolve(callouts);
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.LAtlv);

                    reject();
                });
            });
    }

    /**
     * Returns a promise with an array of the callouts that should be shown to the user.
     * These will be shown after the first run callouts and can be shown after the first run experience has been completed
     * TODO: Decouple these callouts from the first run experience.
     */
    private getReturningUserCallouts(): Promise<Array<FeatureCallout>> {
        const newCalloutsSeen = this.fetchHasSeenNewCallouts();

        return new Promise<Array<FeatureCallout>>((resolve, reject) => {
            Promise.all([
                this.shellShimService.afterShellPlusLoaded,
                this.modalFreClosedPromise,
                this.consumerSubscriptionStatusPromise,
                this.userConsentPromise,
                newCalloutsSeen,
                this.appBarShowingPromise
            ]).then(([shellResult, _, consumerSubscriptionStatus, consentConfig, calloutsSeen]) => {
                    this.detectDomElements();

                    const calloutsPromises = new Array<Promise<FeatureCallout>>();
                    const includeShellCallouts = shellResult;
                    const isFreeConsumer = this.isFreeConsumer(consumerSubscriptionStatus);
                    const userCanViewUpsell = this.userCanViewUpsell(isFreeConsumer, consentConfig);

                    if (!calloutsSeen || this.contextService.sharedContext.inMockMode) { // haven't seen the new first run callouts, should see returning user callouts
                        // TODO: Without a callout element the other callouts are failing
                        calloutsPromises.push(Promise.resolve(
                            new FeatureCallout(
                                this.appLauncherElement,
                                CalloutType.Welcome,
                                this.contextService.isEdgeworth ? KnownAreas.Edgeworth : KnownAreas.Documents,
                                this.locService.FirstRunWelcomeBackTitle,
                                this.locService.FirstRunWelcomeBackSubtitle,
                                null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.welcomeBackSvg[this.theme], false, true, KnownInstrumentationEvents.ReturningUserCalloutsImpression)
                        ));

                        if (includeShellCallouts && this.appLauncherElement) {
                            const isEduUser = this.userContextService.user.isEdu;
                            const findMoreAppsSvg = !this.isCommercialUser ? this.config.findMoreAppsConsumerSvg[this.theme] : isEduUser ? this.config.findMoreAppsEduSvg[this.theme] : this.config.findMoreAppsCommercialSvg[this.theme];

                            calloutsPromises.push(Promise.resolve(
                                new FeatureCallout(
                                    this.appLauncherElement,
                                    CalloutType.AppLauncher,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunFindMoreAppsTitle,
                                    this.locService.FirstRunFindMoreAppsSubtitleReturningUser,
                                    null, null, null, null, null, false, null, null, null, null, null, null, null, findMoreAppsSvg, false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));

                            if (this.createNewButtonElement) {
                                calloutsPromises.push(Promise.resolve(
                                    new FeatureCallout(
                                    this.createNewButtonElement,
                                    CalloutType.NewButton,
                                    KnownAreas.FirstRun,
                                    this.locService.ExpandedCreateCapabilitiesTitle,
                                    this.locService.FirstRunExpandedCreateCapabilitiesSubittle,
                                    null, null, null, null, null, true, null, null, null, null, null, null, null, this.config.createSvg[this.theme], false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            }

                            if (this.myContentButtonElement && this.isCommercialUser) {
                                calloutsPromises.push(Promise.resolve(new FeatureCallout(
                                    this.myContentButtonElement,
                                    CalloutType.MyContent,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunManageContentTitle,
                                    this.locService.FirstRunManageContentSubtitle,
                                    null, null, null, null, null, true, null, null, null, null, null, null, null, this.config.manageContentSvg[this.theme], false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            }

                            if (this.consumerBuyButtonElement) {
                                if (userCanViewUpsell) {
                                    const subtitle = this.locService.FirstRunUpgradeAnyTimeSubtitleTemp;

                                    calloutsPromises.push(Promise.resolve(new FeatureCallout(
                                        this.consumerBuyButtonElement,
                                        CalloutType.BuyOffice,
                                        KnownAreas.FirstRun,
                                        this.locService.FirstRunUpgradeAnyTimeTitle,
                                        subtitle,
                                        null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.upgradeSvg[this.theme], false, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                                }
                            }

                            if (this.feedbackContainerElement) {
                                calloutsPromises.push(Promise.resolve(new FeatureCallout(
                                    this.feedbackContainerElement,
                                    CalloutType.Feedback,
                                    KnownAreas.FirstRun,
                                    this.locService.FirstRunYoureAllSetTitle,
                                    this.locService.FirstRunYoureAllSetSubtitleReturningUser,
                                    null, null, null, null, null, false, null, null, null, null, null, null, null, this.config.allSetSvg[this.theme], true, false, KnownInstrumentationEvents.ReturningUserCalloutsImpression)));
                            }
                        }

                        const params = {};
                        params[InstrumentationParam.FirstRunTotalSteps] = calloutsPromises.length.toString();
                        params[InstrumentationParam.State] = 'after-first-run';
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FreNewCalloutsEvent, params);
                        // TODO: Remove above logger once OTEL migration is complete
                        logFeature({
                            featureName: KnownFeatureNames.FirstRun,
                            eventName: KnownInstrumentationEvents.FreNewCalloutsEvent,
                            params
                        });

                        this.userProfileService.incrementMetric(KnownInstrumentationEvents.FreNewCalloutsImpression);
                    }

                    Promise.all(calloutsPromises).then(callouts => resolve(callouts.filter(callout => callout !== null)));
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.Cgjwv);

                    reject();
                });
        });
    }

    private isFreeConsumer(consumerSubscriptionStatus: ConsumerSkuType): boolean {
        return !this.isCommercialUser
            && (consumerSubscriptionStatus === ConsumerSkuType.NonActive || consumerSubscriptionStatus === ConsumerSkuType.NotSubscribed);
    }

    private userCanViewUpsell(isFreeConsumer: boolean, consentConfig: UserConsentConfig): boolean {
        return isFreeConsumer
            && (!consentConfig || consentConfig.userConsent === ConsumerUserConsentGroup.CanConsent);
    }

    /**
     * Adds appropriate actions to each callout in the first run experience.
     * Adds a Done button for last action that dismisses callout, adds Next and Back buttons to others to navigate a series of callouts.
     * @param callouts: The array of callouts that comprise the first run experience
     */
    private initCalloutActions(callouts: Array<FeatureCallout>) {
        const numCallouts = callouts.length;
        const registerButtonClick = (stepsCompleted: number) => {
            const nextCallout = callouts[stepsCompleted];
            if (nextCallout) {
                this.firstRunDataService.updateFirstRunData({CompletedFirstRun: false, StepsCompleted: stepsCompleted + 1, Version: this.firstRunVersion, TotalSteps: numCallouts}, FirstRunDataService.firstRunDataKey, nextCallout.calloutType);
            }
        };

        callouts.forEach((callout, index) => {
            callout.buttonIdPrefix = `first-run-step-${index}-${callout.calloutType.toLowerCase()}`;
            callout.onAction = index === (numCallouts - 1)
                ? (action: FeatureCalloutAction) => {
                    if (action === FeatureCalloutAction.None) {
                        return;
                    } else if (action === FeatureCalloutAction.Back) {
                        registerButtonClick(index - 1);
                    } else {
                        this.firstRunDataService
                            .updateFirstRunData({CompletedFirstRun: true, StepsCompleted: numCallouts, Version: this.firstRunVersion, TotalSteps: numCallouts}, FirstRunDataService.firstRunDataKey);
                    }
                }
                : (action: FeatureCalloutAction) => {
                    if (action === FeatureCalloutAction.None) {
                        return;
                    } else if (action === FeatureCalloutAction.Close) {
                        this.firstRunDataService.updateFirstRunData({ CompletedFirstRun: true, StepsCompleted: index + 1, Version: this.firstRunVersion, TotalSteps: numCallouts }, FirstRunDataService.firstRunDataKey);
                    } else {
                        registerButtonClick(action === FeatureCalloutAction.Next ? index + 1 : index - 1);
                    }
                };
        });
    }

    /**
     * Determines which callout in series user should first be presented with. Sets all callouts before it as completed.
     * @param firstRunData: The recorded current state of the first run experience
     * @param callouts: The array of callouts that comprise the first run experience
     */
     private setupFirstCallout(firstRunData: FirstRunData, callouts: Array<FeatureCallout>) {
        // TODO: Evaluate if both FurthestCompleted and StepsCompleted properties are necessary
        const startPoint = firstRunData.Version !== this.firstRunVersion
            ? 0
            : typeof (firstRunData.FurthestCompleted) !== 'undefined' && firstRunData.FurthestCompleted < callouts.length
                ? firstRunData.FurthestCompleted
                : firstRunData.StepsCompleted < callouts.length
                    ? firstRunData.StepsCompleted
                    : 0;

        for (let i = 0; i < startPoint; i++) {
            callouts[i].completed = true;
        }

        const firstCallout = callouts[startPoint];

        this.firstRunDataService.updateFirstRunData(
            {
                CompletedFirstRun: false,
                StepsCompleted: startPoint + 1,
                Version: this.firstRunVersion,
                TotalSteps: callouts.length
            },
            FirstRunDataService.firstRunDataKey,
            firstCallout.calloutType
        );
    }

    private detectDomElements() {
        this.appLauncherElement = this.documentService.nativeDocument.getElementById('O365_MainLink_NavMenu');
        this.adminCenterButtonElement =
            this.documentService.nativeDocument.getElementById('A28790FE-989B-48F6-90D8-D4996159C8D1') ||
            this.documentService.nativeDocument.getElementById('ShellAdmin_link');
        this.createNewButtonElement =
            this.documentService.nativeDocument.getElementById('83D3F491-B586-4266-8738-89776471BF21') ||
            this.documentService.nativeDocument.getElementById('left-nav-create-center');
        this.myContentButtonElement = this.documentService.nativeDocument.getElementById('F57D5D29-4967-495C-A648-F7AB66BF8572');
        this.uploadButtonElement =
            this.documentService.nativeDocument.getElementById('upload-button-Edgeworth') ||
            this.documentService.nativeDocument.getElementById('upload-button-QuickAccess') ||
            this.documentService.nativeDocument.getElementById('upload-button');
        this.installButtonElement =
            this.documentService.nativeDocument.getElementById('install-dropdown-link') ||
            this.documentService.nativeDocument.getElementById('installbutton-MSA-RedirectToMyAccountPage');
        this.consumerBuyButtonElement = this.documentService.nativeDocument.getElementById('consumer-buy-button');
        this.feedbackContainerElement = this.documentService.nativeDocument.getElementById('feedbackContainer');
    }
}
